/**
 * RoutinePickerDialog — Slice 9 overload preview dialog.
 *
 * PRD: Ralph/NavigationalFeatures/PRD.md § Slice 9 (routine) — overload
 * preview dialog follow-up.
 *
 * F12 on a routine call where the OE tree carries multiple matches (PG /
 * Oracle overloads sharing a name and schema) opens this dialog. The user
 * picks one row; we then route through `navigateToRoutine(...)` exactly as
 * the single-candidate path does.
 *
 * Shape mirrors `ConnectionSelectorDialog`: keyboard-driven (↑ / ↓ / Enter),
 * mouse-hover updates selection, click-to-pick. Selection auto-scrolls into
 * view.
 */
import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { cn } from '../../../lib/utils';
import { useRoutinePickerStore } from './routinePickerStore';
import { navigateToRoutine } from './routineNavigation';
import type { RoutinePickerCandidate, RoutinePickerContext } from './routinePickerStore';

export function RoutinePickerDialog() {
  const isOpen = useRoutinePickerStore((s) => s.isOpen);
  const candidates = useRoutinePickerStore((s) => s.candidates);
  const context = useRoutinePickerStore((s) => s.context);
  const closePicker = useRoutinePickerStore((s) => s.closePicker);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (isOpen) setSelectedIndex(0);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, isOpen]);

  if (!context) return null;

  const handleSelect = (candidate: RoutinePickerCandidate) => {
    pickAndNavigate(candidate, context);
    closePicker();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (candidates.length === 0) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, candidates.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        event.preventDefault();
        if (candidates[selectedIndex]) handleSelect(candidates[selectedIndex]);
        break;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closePicker()}>
      <DialogContent
        className="sm:max-w-[460px]"
        data-testid="routine-picker-dialog"
        onKeyDown={handleKeyDown}
      >
        <DialogHeader>
          <DialogTitle>Pick a routine overload</DialogTitle>
          <DialogDescription>
            Multiple routines share this name. Choose which one to open.
          </DialogDescription>
        </DialogHeader>

        <div
          className="py-2 max-h-[320px] overflow-y-auto"
          role="listbox"
          aria-label="Routine candidates"
        >
          {candidates.map((candidate, index) => (
            <button
              key={candidateKey(candidate, index)}
              ref={(el) => {
                itemRefs.current[index] = el;
              }}
              type="button"
              role="option"
              aria-selected={index === selectedIndex}
              onClick={() => handleSelect(candidate)}
              onMouseEnter={() => setSelectedIndex(index)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors',
                'focus:outline-none focus-visible:ring-1 focus-visible:ring-ring/30',
                index === selectedIndex
                  ? 'bg-accent text-accent-foreground'
                  : 'hover:bg-accent/50',
              )}
              data-testid={`routine-candidate-${index}`}
            >
              <span
                className={cn(
                  'shrink-0 inline-flex items-center justify-center text-xs font-medium px-1.5 py-0.5 rounded',
                  candidate.routineKind === 'stored-procedure'
                    ? 'bg-blue-500/15 text-blue-700 dark:text-blue-300'
                    : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
                )}
              >
                {candidate.routineKind === 'stored-procedure' ? 'PROC' : 'FN'}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">
                  {qualifiedDisplay(candidate)}
                </div>
                {candidate.objectId && (
                  <div className="text-xs text-muted-foreground truncate">
                    id: {candidate.objectId}
                  </div>
                )}
              </div>
              {index === selectedIndex && (
                <Check className="h-4 w-4 text-primary shrink-0" />
              )}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function pickAndNavigate(
  candidate: RoutinePickerCandidate,
  context: RoutinePickerContext,
): void {
  navigateToRoutine({
    qualified: candidate.binding.target,
    routineKind: candidate.routineKind,
    engineId: context.engineId,
    connectionId: context.connectionId,
    database: context.database,
    objectId: candidate.objectId,
  });
}

function qualifiedDisplay(candidate: RoutinePickerCandidate): string {
  const target = candidate.binding.target;
  return [target.schema, target.name].filter(Boolean).join('.') || target.name;
}

function candidateKey(candidate: RoutinePickerCandidate, index: number): string {
  if (candidate.objectId) return candidate.objectId;
  const target = candidate.binding.target;
  return `${candidate.routineKind}-${target.schema ?? ''}-${target.name}-${index}`;
}
